<template>
  <!-- Main Content -->
  <main class="py-4 sm:py-8 sm:p-8">
    <!-- Header -->
    <div class="mb-10 flex items-center justify-between">
      <div>
        <PageTitle>
          Gerencie grupos e canais
        </PageTitle>
        <PageDescription>
          Expanda seu plano de assinatura com mais grupos ou canais. Configure planos de pagamento e otimize a experiência dos seus assinantes.
        </PageDescription>
      </div>
      <RegisterButton @click="openNewGroupModal">Cadastrar grupo</RegisterButton>
    </div>

    <!-- Groups Table Card -->
    <TableContainer>
      <!-- Table Header -->
      <div class="mb-6 flex items-center justify-between">
        <h2 class="text-xl font-semibold text-white">Grupos e canais registrados</h2>
        
        <div class="flex items-center gap-4">
          <!-- <div class="relative">
            <Input
              v-model="searchQuery"
              placeholder="Buscar grupos..."
              class="w-[280px]"
            >
              <template #prefix>
                <MagnifyingGlassIcon class="h-4 w-4 text-gray-400" />
              </template>
            </Input>
          </div> -->
          
          <!-- <Select v-model="planFilter">
            <SelectTrigger class="w-[280px]">
              <SelectValue placeholder="Selecione o plano de pagamento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os planos</SelectItem>
              <SelectItem v-for="plan in paymentPlans" :key="plan.id" :value="plan.id">
                {{ plan.name }}
              </SelectItem>
            </SelectContent>
          </Select> -->
        </div>
      </div>

      <!-- Table -->
      <Table class="hidden md:block">
        <TableHeader>
          <TableRow>
            <TableHead
              v-for="header in tableHeaders"
              :key="header.key"
              :class="header.class"
            >
              <div class="flex items-center gap-2">
                {{ header.label }}
                <ArrowsUpDownIcon 
                  v-if="header.sortable"
                  class="h-4 w-4 cursor-pointer text-gray-500 hover:text-white"
                  @click="sortBy(header.key)"
                />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <template v-if="groups">
            <TableFallback v-if="groups.data.length === 0" :colspan="tableHeaders.length" :icon="UsersIcon" title="Nenhum grupo ou canal encontrado" :description="`Clique em 'Cadastrar grupo' para começar`" />
            <TableRow
              v-for="group in groups.data"
              :key="group.id"
            >
              <TableCell>
                <div>
                  <div class="text-sm font-medium text-white text-wrap">{{ group.name }}</div>
                </div>
              </TableCell>
              <TableCell>
                <div class="flex items-center gap-2">
                  <!-- <template> -->
                    <div class="w-48 truncate font-mono text-xs text-zinc-400">
                      {{ `https://tinyurl.com/3avpxfmx/${group.uuid}` }}
                    </div>
                    <button
                      @click="copyGroupLink(group)"
                      class="rounded-lg bg-white/5 p-1.5 text-zinc-400 transition-all hover:bg-white/10 hover:text-white group-hover:opacity-100"
                    >
                      <ClipboardIcon class="h-4 w-4 text-white" />
                    </button>
                  <!-- </template> -->
                  <!-- <PulseBadge v-else variant="yellow">Nenhum token reserva configurado</PulseBadge> -->
                </div>
              </TableCell>
              <TableCell>
                <div>
                  <div class="text-sm font-medium text-white">{{ GroupCategoryLabels[group.category] }}</div>
                </div>
              </TableCell>
              <TableCell>
                <div class="flex flex-wrap gap-2">
                  <Badge 
                    v-for="plan in group.paymentPlans" 
                    :key="plan.id"
                    variant="default"
                  >
                    {{ plan.name }}
                  </Badge>
                </div>
              </TableCell>
              <TableCell>
                <div class="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    @click="editGroup(group)"
                  >
                    <PencilIcon class="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="hover:text-primary"
                    @click="openMigrateModal(group)"
                    title="Migrar leads"
                  >
                    <ArrowRightLeft class="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="hover:text-red-400"
                    @click="deleteGroup(group)"
                  >
                    <TrashIcon class="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </template>
          <TableRowSkeleton :columns="tableHeaders.length" v-else />
        </TableBody>
      </Table>

      <!-- Mobile List -->
      <div class="space-y-4 md:hidden text-left" v-if="groups">
        <template v-if="groups.data.length > 0">
          <MobileCard v-for="group in groups.data" :key="group.id">
            <template #header>
               <div class="flex items-center gap-2">
                 <div class="flex flex-col">
                  <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider">Grupo</span>
                  <div class="flex items-center gap-2">
                    <span class="text-white font-bold text-lg">{{ group.name }}</span>
                  </div>
                 </div>
               </div>
            </template>
            
            <template #actions>
               <div class="flex flex-col items-end gap-2">
                  <div class="flex items-center gap-1">
                    <button @click="editGroup(group)" class="text-zinc-400 hover:text-white p-1">
                      <PencilIcon class="size-5" />
                    </button>
                    <button @click="deleteGroup(group)" class="text-zinc-400 hover:text-red-400 p-1">
                      <TrashIcon class="size-5" />
                    </button>
                  </div>
               </div>
            </template>
            
            <template #content>
               <MobileDataLabel label="Link do grupo">
                  <div class="flex items-center gap-2 bg-white/5 p-2 rounded justify-between">
                     <div class="font-mono text-xs text-zinc-400 truncate max-w-[250px]">
                       {{ `https://tinyurl.com/3avpxfmx/${group.uuid}` }}
                     </div>
                     <button
                        @click="copyGroupLink(group)"
                        class="rounded-lg p-1.5 text-zinc-400 transition-all hover:bg-white/10 hover:text-white"
                      >
                        <ClipboardIcon class="size-4 text-white" />
                      </button>
                  </div>
               </MobileDataLabel>

               <div class="flex items-center justify-between pt-2">
                  <div class="flex flex-col">
                    <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1">Categoria</span>
                    <div class="text-white text-sm">{{ GroupCategoryLabels[group.category] }}</div>
                  </div>
               </div>
               
               <MobileDataLabel label="Planos de pagamento" v-if="group.paymentPlans?.length">
                  <div class="flex flex-wrap gap-1">
                    <Badge 
                      v-for="plan in group.paymentPlans" 
                      :key="plan.id"
                      variant="default"
                      class="text-xs"
                    >
                      {{ plan.name }}
                    </Badge>
                  </div>
               </MobileDataLabel>
            </template>
          </MobileCard>
        </template>
        <div v-else class="text-center py-12 text-zinc-500">
           <UsersIcon class="size-8 mx-auto mb-3 text-zinc-700" />
           Nenhum grupo ou canal encontrado
        </div>
      </div>
      <!-- Pagination -->
      <Pagination @update:page="(page: string) => reloadAndGetAll({ page })" v-model:per-page="perPage" :data="groups?.pagination" />
    </TableContainer>
  </main>

  <DeleteConfirmationDialog v-model:open="isDeleteGroupDialogOpen" :loading="isDeletingGroup" @confirm="isDeletingGroup = true; destroy(deletingGroup!).then(() => {
    toast({
      title: 'Grupo excluído com sucesso',
      class: 'bg-green-500 text-white',
      duration: 1500
    })
    isDeletingGroup = false
    isDeleteGroupDialogOpen = false
    const index = groups?.data.findIndex(group => group.id == deletingGroup)
    if (index !== -1) groups?.data.splice(index!, 1)
  })" />

  <!-- New/Edit Group Modal -->
  <Dialog v-model:open="isGroupModalOpen">
    <DialogScrollContent class="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>{{ editingGroup ? 'Editar grupo ou canal' : 'Novo grupo ou canal' }}</DialogTitle>
        <DialogDescription>
          Configure as informações do grupo ou canal e selecione os planos de pagamento.
        </DialogDescription>
      </DialogHeader>
      <div class="grid grid-cols-2 gap-6 py-4">
        <div class="grid gap-2 col-span-full">
          <Label>Nome do grupo/canal <span class="text-red-500">*</span></Label>
          <Input v-model="groupForm.name" placeholder="Ex: VIP" />
        </div>
        <div class="grid gap-2 col-span-full">
          <Label class="flex items-center gap-2">
            ID do grupo/canal <span class="text-red-500">*</span>
            <TooltipProvider>
              <Tooltip :delay-duration="100">
                <TooltipTrigger as-child>
                  <CircleHelp class="size-5 text-primary" />
                </TooltipTrigger>
                <TooltipContent side="top" :side-offset="5">
                  O ID do grupo só é válido para <span class="text-emerald-500">super grupos</span> e <span class="text-emerald-500">canais</span>. <br>
                  Para converter um <span class="text-red-500">grupo comum</span> para <span class="text-emerald-500">super grupo</span> basta <br> 
                  adicionar seu bot como admin com todas as permissões <br> ao grupo e em seguida obter o novo ID.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Label>
          <Input v-model="groupForm.groupId" type="string" placeholder="Ex: 1234" />
        </div>
        <div class="grid gap-2 col-span-full">
          <Label>Categoria <span class="text-red-500">*</span></Label>
          <Select v-model="groupForm.category" multiple>
            <SelectTrigger>
              <SelectValue placeholder="Selecione a categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem 
                v-for="category in GroupCategoryEnum" 
                :key="category" 
                :value="category"
              >
                {{ GroupCategoryLabels[category] }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div v-if="groupForm.category === GroupCategoryEnum.PAID" class="grid gap-2 col-span-full">
          <Label>Plano</Label>
          <TagsInput v-if="filteredPaymentPlans" class="px-0 gap-0 w-full" :model-value="paymentPlanNames">
            <div class="flex gap-2 flex-wrap items-center px-3">
              <TagsInputItem v-for="(name, index) in paymentPlanNames" :key="index" :value="name">
                <TagsInputItemText />
                <TagsInputItemDelete @click="groupForm.paymentPlanIds?.splice(index, 1); paymentPlanNames.splice(index, 1)" />
              </TagsInputItem>
            </div>
            
            <ComboboxRoot v-model="paymentPlanNames" v-model:open="isPaymentPlanComboboxOpen" v-model:search-term="searchPaymentPlan" class="w-full">
              <ComboboxAnchor as-child>
                <ComboboxInput placeholder="Plano premium..." as-child>
                  <TagsInputInput @focus="isPaymentPlanComboboxOpen = true" class="w-full px-3" :class="(groupForm.paymentPlanIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                </ComboboxInput>
              </ComboboxAnchor>
        
              <ComboboxPortal>
                <ComboboxContent>
                  <CommandList
                    position="popper"
                    class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                  >
                    <CommandEmpty>Nenhum plano encontrado</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        v-for="paymentPlan in filteredPaymentPlans" :key="paymentPlan.id" :value="String(paymentPlan.name)"
                        @select.prevent="(ev) => {
                          if (typeof ev.detail.value === 'string') {
                            searchPaymentPlan = ''
                            const paymentPlan = filteredPaymentPlans?.find(plan => plan.name == ev.detail.value)
                            groupForm.paymentPlanIds?.push(String(paymentPlan!.id))
                            paymentPlanNames.push(paymentPlan!.name)
                          }
        
                          if (filteredPaymentPlans?.length === 0) {
                            isPaymentPlanComboboxOpen = false
                          }
                        }"
                      >
                        {{ paymentPlan.name }}
                      </CommandItem>
                    </CommandGroup>
                  </CommandList>
                </ComboboxContent>
              </ComboboxPortal>
            </ComboboxRoot>
          </TagsInput>
          <p class="text-xs text-gray-400">Você pode selecionar mais de 1 plano</p>
        </div>
        <!-- <div v-if="groupForm.category === GroupCategoryEnum.FREE" class="grid gap-2 col-span-full">
          <Label>Link de acesso</Label>
          <Input v-model="groupForm.accessUrl" placeholder="Ex: https://url..." />
        </div>
        <div v-if="groupForm.category === GroupCategoryEnum.FREE" class="grid gap-2 col-span-full sm:col-span-1">
          <Label>Aceitar novos membros após</Label>
          <Input type="number" v-model="groupForm.newMembersAcceptanceDelay" placeholder="Ex: 10" />
        </div>
        <div v-if="groupForm.category === GroupCategoryEnum.FREE" class="grid gap-2 col-span-full sm:col-span-1">
          <Label>Unidade de tempo</Label>
          <Select v-model="timeUnit">
            <SelectTrigger>
              <SelectValue placeholder="Selecione os planos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="TimeUnitEnum.SECONDS">Segundos</SelectItem>
              <SelectItem :value="TimeUnitEnum.MINUTES">Minutos</SelectItem>
              <SelectItem :value="TimeUnitEnum.HOURS">Horas</SelectItem>
              <SelectItem :value="TimeUnitEnum.DAYS">Dias</SelectItem>
            </SelectContent>
          </Select>
        </div> -->
        <div class="grid gap-2 col-span-full">
          <Label class="flex items-center gap-2">
            Mensagem de boas vindas
            <TooltipProvider>
              <Tooltip :delay-duration="100">
                <TooltipTrigger as-child>
                  <CircleHelp class="size-5 text-primary" />
                </TooltipTrigger>
                <TooltipContent side="top" :side-offset="5">
                  <span class="text-primary" v-html="'{{nome}}'"></span>: Nome do novo membro<br>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Label>
          <Textarea v-model="groupForm.welcomeMessage" placeholder="Ex: Bem-vindo ao grupo" />
        </div>
        <div v-if="groupForm.category === GroupCategoryEnum.FREE" class="flex items-center gap-2">
          <Switch v-model:checked="groupForm.newMembersAutomaticApproval" />
          <Label>Aprovação automática</Label>
        </div>
        <div v-if="groupForm.category === GroupCategoryEnum.FREE && groupForm.newMembersAutomaticApproval" class="flex items-center gap-2 col-span-full text-white text-sm">
          <InfoIcon />
          É possível cadastrar uma mensagem que será enviada no privado do lead após a aprovação na aba de disparo de mensagens
        </div>
      </div>
      <DialogFooter>
        <Button class="text-white" variant="ghost" @click="closeGroupModal">Cancelar</Button>
        <Button :disabled="isSaving || !groupForm.name || !groupForm.groupId || !groupForm.category || (groupForm.category === GroupCategoryEnum.PAID && !groupForm.paymentPlanIds?.length)" @click="save">
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          {{ editingGroup ? 'Salvar alterações' : 'Cadastrar grupo' }}
        </Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>
  <!-- Migrate Leads Modal -->
  <Dialog v-model:open="isMigrateModalOpen">
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Migrar Leads</DialogTitle>
        <DialogDescription>
          Selecione o grupo de destino para migrar todos os leads de <strong>{{ migratingFromGroup?.name }}</strong>.
        </DialogDescription>
      </DialogHeader>
      <div class="grid gap-4 py-4">
        <div class="grid gap-2">
          <Label>Grupo de destino</Label>
          <Select v-model="migrateToGroupId">
            <SelectTrigger>
              <SelectValue placeholder="Selecione o grupo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem 
                v-for="group in availableTargetGroups" 
                :key="group.id" 
                :value="String(group.id)"
              >
                {{ group.name }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div v-if="migrateToGroupId === String(migratingFromGroup?.id)" class="flex items-center gap-2 text-blue-400 text-xs">
          <InfoIcon class="size-4" />
          Você está migrando para o mesmo registro de grupo. Isso é útil caso você tenha alterado o ID do grupo nas configurações e queira reenviar o link para os assinantes.
        </div>
        <div class="flex items-center gap-2 text-amber-500 text-xs">
          <InfoIcon class="size-4" />
          Esta ação migrará todos os assinantes do grupo atual para o selecionado.
        </div>
      </div>
      <DialogFooter>
        <Button class="text-white" variant="ghost" @click="isMigrateModalOpen = false">Cancelar</Button>
        <Button :disabled="!migrateToGroupId || isMigrating" @click="confirmMigration">
          <Loader2 v-if="isMigrating" class="w-4 h-4 animate-spin mr-2" />
          Confirmar migração
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import {
  ArrowsUpDownIcon,
  UsersIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import RegisterButton from '@/components/ui/register-button/RegisterButton.vue'
import Table from '@/components/ui/table/Table.vue'
import TableCell from '@/components/ui/table/TableCell.vue'
import TableFallback from '@/components/ui/table/TableFallback.vue'
import TableRow from '@/components/ui/table/TableRow.vue'
import TableBody from '@/components/ui/table/TableBody.vue'
import TableHeader from '@/components/ui/table/TableHeader.vue'
import TableHead from '@/components/ui/table/TableHead.vue'
import TableContainer from '@/components/ui/table/TableContainer.vue'
import { CreateGroupDTO, GetAllGroupsParams, UpdateGroupDTO, useGroup } from '@/composables/useGroup'
import { Group } from '@/interfaces/Group'
import { Pagination as IPagination } from '@/interfaces/Pagination'
import TableRowSkeleton from '@/components/ui/table/TableRowSkeleton.vue'
import { GroupCategoryEnum, GroupCategoryLabels } from '@/enums/groups/GroupCategoryEnum'
import { usePaymentPlan } from '@/composables/usePaymentPlan'
import { PaymentPlan } from '@/interfaces/PaymentPlan'
import { ArrowRightLeft, CircleHelp, ClipboardIcon, InfoIcon, Loader2, PlusIcon } from 'lucide-vue-next'
import MobileCard from '@/components/ui/mobile-card/MobileCard.vue'
import MobileDataLabel from '@/components/ui/mobile-card/MobileDataLabel.vue'
import { useToast } from '@/components/ui/toast'
import { TagsInput, TagsInputItem, TagsInputItemDelete, TagsInputItemText } from '@/components/ui/tags-input'
import { ComboboxAnchor, ComboboxContent, ComboboxInput, ComboboxPortal, ComboboxRoot } from 'radix-vue'
import TagsInputInput from '@/components/ui/tags-input/TagsInputInput.vue'
import { CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { AcceptableInputValue } from 'radix-vue/dist/TagsInput/TagsInputRoot'
import DeleteConfirmationDialog from '@/components/ui/delete-dialog/DeleteConfirmationDialog.vue'
import { useTime } from '@/composables/useTime'
import { TimeUnitEnum, TimeUnitLabels } from '@/enums/TimeUnitEnum'
import Pagination from '@/components/ui/pagination/Pagination.vue'
import PageTitle from '@/components/ui/page/PageTitle.vue'
import PageDescription from '@/components/ui/page/PageDescription.vue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Switch } from '@/components/ui/switch'
import { RouterLink } from 'vue-router'
import { MessageRecipientTypeEnum } from '@/enums/messages/MessageRecipientTypeEnum'

const { toast } = useToast()
const { create, update, getAll, destroy, migrateLeads } = useGroup()
const { getAll: getAllPaymentPlans } = usePaymentPlan()
const paymentPlans = ref<IPagination<PaymentPlan>>()
// Table Configuration
const tableHeaders = [
  { key: 'name', label: 'Nome', sortable: false },
  { key: 'link', label: 'Link', sortable: false },
  { key: 'category', label: 'Categoria', sortable: false },
  { key: 'plans', label: 'Planos de pagamento', sortable: false },
  { key: 'actions', label: '', class: 'w-32' },
]
const isSaving = ref(false)
const perPage = ref('10')

// Filters
const filteredPaymentPlans = computed(() => paymentPlans.value?.data.filter(i => !groupForm.value.paymentPlanIds?.includes(String(i.id))))
const searchPaymentPlan = ref('')
const isPaymentPlanComboboxOpen = ref(false)
// Groups data (empty for demo)
const groups = ref<IPagination<Group>>()
const paymentPlanNames = ref<string[]>([])
// Modal and Form
const isGroupModalOpen = ref(false)
const editingGroup = ref<number | null>(null)
const groupForm = ref<Partial<CreateGroupDTO>>({
  name: '',
  groupId: undefined,
  paymentPlanIds: []
})
const timeUnit = ref<TimeUnitEnum>(TimeUnitEnum.SECONDS)
const { convertToSeconds, convertFromSeconds, convertIntervalToSeconds, convertToInterval } = useTime()
const deletingGroup = ref<number>()
const isDeleteGroupDialogOpen = ref(false)
const isDeletingGroup = ref(false)

// Migration
const isMigrateModalOpen = ref(false)
const isMigrating = ref(false)
const migratingFromGroup = ref<Group | null>(null)
const migrateToGroupId = ref<string>('')
const availableTargetGroups = computed(() => groups.value?.data || [])

// Actions
const openNewGroupModal = () => {
  editingGroup.value = null
  groupForm.value = {
    name: '',
    paymentPlanIds: []
  }
  isGroupModalOpen.value = true
}

const closeGroupModal = () => {
  isGroupModalOpen.value = false
}

const editGroup = (group: Group) => {
  editingGroup.value = group.id
  const delayInSeconds = group.newMembersAcceptanceDelay ? convertIntervalToSeconds(group.newMembersAcceptanceDelay) : 0
  const { value: newMembersAcceptanceDelay, unit } = convertFromSeconds(delayInSeconds);

  timeUnit.value = unit;
  groupForm.value = {
    name: group.name,
    groupId: group.groupId,
    category: group.category,
    newMembersAcceptanceDelay,
    welcomeMessage: group.welcomeMessage,
    paymentPlanIds: ((group.paymentPlans?.map(plan => String(plan.id)) ?? []) as unknown as AcceptableInputValue[]),
    newMembersAutomaticApproval: group.newMembersAutomaticApproval
  }
  paymentPlanNames.value = group.paymentPlans?.map(plan => plan.name) ?? []
  isGroupModalOpen.value = true
}

const deleteGroup = (group: any) => {
  isDeleteGroupDialogOpen.value = true
  deletingGroup.value = group.id
}

const openMigrateModal = (group: Group) => {
  migratingFromGroup.value = group
  migrateToGroupId.value = ''
  isMigrateModalOpen.value = true
}

const confirmMigration = async () => {
  if (!migratingFromGroup.value || !migrateToGroupId.value) return

  isMigrating.value = true
  try {
    await migrateLeads(migratingFromGroup.value.id, Number(migrateToGroupId.value))
    
    toast({
      title: 'Migração iniciada',
      description: 'O processo de migração foi enfileirado e será processado em breve.',
      class: 'bg-green-500 text-white',
      duration: 3000
    })
    
    isMigrateModalOpen.value = false
  } catch (error) {
    toast({
      title: 'Erro ao migrar leads',
      description: 'Ocorreu um erro ao tentar iniciar a migração.',
      variant: 'destructive',
      duration: 3000
    })
  } finally {
    isMigrating.value = false
  }
}

const sortBy = (key: string) => {
  // Implement sorting logic
}

const copyGroupLink = async (group: Group) => {
  const link = `https://tinyurl.com/3avpxfmx/${group.uuid}`
  await navigator.clipboard.writeText(link)
  toast({
    title: 'Link copiado para a área de transferência',
    class: 'bg-green-500 text-white',
    duration: 1500
  })
}

const save = async () => {
  isSaving.value = true

  try {
    let payload = { ...groupForm.value }
    
    if (payload.newMembersAcceptanceDelay && timeUnit.value) {
      payload.newMembersAcceptanceDelay = convertToSeconds(payload.newMembersAcceptanceDelay, timeUnit.value)
    }

    if (!editingGroup.value) {
      const group = await create(payload as CreateGroupDTO)
      toast({
        title: 'Grupo criado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
      groups.value?.data.push(group)
    } else {
      await update(editingGroup.value, payload as UpdateGroupDTO)
      toast({
        title: 'Grupo editado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })

      const _paymentPlans = paymentPlans.value?.data.filter(plan => payload.paymentPlanIds?.includes(String(plan.id))) ?? []
      const index = groups.value!.data.findIndex(group => group.id == editingGroup.value)
      
      if (index !== -1 && index !== null) {
        groups.value!.data[index] = { 
          ...groups.value!.data[index], 
          ...groupForm.value,
          paymentPlans: _paymentPlans,
          newMembersAcceptanceDelay: payload.newMembersAcceptanceDelay
            ? convertToInterval(payload.newMembersAcceptanceDelay, TimeUnitEnum.SECONDS)
            : null
        } as Group
      }
    }

    isGroupModalOpen.value = false
  } finally {
    isSaving.value = false
  }
}

function reloadAndGetAll(params: GetAllGroupsParams) {
  groups.value = undefined
  getAll(params)
    .then(data => groups.value = data)
}

watch(() => perPage.value, () => {
  reloadAndGetAll({ perPage: perPage.value })
})

watch(() => isGroupModalOpen.value, () => {
  if (!paymentPlans.value) {
    getAllPaymentPlans({ perPage: '50' })
      .then((data) => paymentPlans.value = data)
  }
})

onMounted(() => {
  getAll()
    .then((data) => groups.value = data)
})
</script>